import {
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Event as RouterEvent, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { from, Observable, of, ReplaySubject, timer } from 'rxjs';
import { delay, filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { LanguageService } from '@services/language.service';
import { GenericService } from '@services/generic.service';
import { ApiService } from '@services/api.service';
import { FetchLegalTermsAndPrivacyPolicyUpdatedDate, FetchLegalTermsAndPrivacyPolicyUpdatedDateSuccess } from './shared/state';
import { LegalTermsService } from './shared/services/legal-terms/legal-terms.service';
import { DOCUMENT } from '@angular/common';
import { DEFAULT_STATUS_PING_TIME, LOADING_STARTED_KEY } from '@constants/constants';
import { LoggerService } from '@services/logger.service';
import { ISidebarOptions } from '@interfaces/sidebar-options';
import { AppService } from '@app/app.service';
import { enumEnvironmentName } from '@env/enumEnvironmentName';
import { environment } from '@env/environment';
import { SwUpdate } from '@angular/service-worker';
import { CheckForUpdateService } from '@services/check-update-pwa.service';
import { GtmService, LocatorService } from '@app/shared/services';
import { RequestLoaderService } from '@services/request-loader.service';
import { CompaniesService } from '@services/companies.service';
import { WidgetsService } from '@services/widgets.service';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { enumUserBetaFlag } from '@data-layer/enums/user';
import { BatchImportRepository } from '@data-layer/repositories';
import { enumApplicationMode } from '@enum/enumApplicationMode';
import { ApplicationModeService } from '@services/application-mode.service';
import { BatchImportModel, PaginatedResultModel } from '@data-layer/models/common';
import { BatchImportService } from '@shared/batch-import-mutual/services';
import { AppOfflineService } from '@app/app-offline.service';
import { TokenService } from '@services/token.service';
import { FetchAppBetaFlags } from '@app/shared/shared-state';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { LhcbMigrationPopupService } from '@services/lhcb-migration-popup.service';

declare let Cookiebot: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public isSidebarOpen: boolean;
  public isLg: boolean;
  public isXlg: boolean;
  public isPurple: boolean;
  public isWindowMd: boolean;
  public isWindowFillMobile: boolean;
  public hideBackDrop: boolean;
  public customZIndex: string;
  public sidebarAsWindowCustomZIndex: string;
  public sidebarHeaderText: string;
  public isSidebarHeaderTitleCase: boolean;
  public tooltipText: string;
  @ViewChild('sidebarForm', { read: ViewContainerRef, static: false })
  public sidebarForm: ViewContainerRef;

  public isSidebarAsWindowOpen: boolean;
  public sidebarAsWindowHeaderText: string;
  @ViewChild('sidebarAsWindowForm', { read: ViewContainerRef, static: false })
  public sidebarAsWindowForm: ViewContainerRef;

  public title: string = 'eezyimport';
  private currentPath: string;
  private diffTimeAfterViewInit: number;

  private legalTermsDate: string;
  private privacyPolicyDate: string;

  private isLoadingBodyClass: string = 'is-loading';

  private consentScriptId: string = 'Cookiebot';

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private langService: LanguageService,
    private router: Router,
    private genericService: GenericService,
    private apiService: ApiService,
    private gtmService: GtmService,
    private ga: GoogleAnalyticsService,
    private store: Store,
    private actions$: Actions,
    private legalTermsService: LegalTermsService,
    @Inject(DOCUMENT) private document: Document,
    private loggerService: LoggerService,
    private appService: AppService,
    private swUpdate: SwUpdate,
    private checkSwUpdate: CheckForUpdateService,
    private injector: Injector,
    private uiService: RequestLoaderService,
    private companiesService: CompaniesService,
    private renderer2: Renderer2,
    private widgetsService: WidgetsService,
    private batchImportRepository: BatchImportRepository,
    private applicationModeService: ApplicationModeService,
    private batchImportService: BatchImportService,
    private appOfflineService: AppOfflineService,
    private tokenService: TokenService,
    private lhcbMigrationPopupService: LhcbMigrationPopupService,
  ) {
    this.checkSwUpdate.checkForUpdates();
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event: Event) {
    this.setParamsToGenericService(event);
  }

  @HostListener('window:wheel', ['$event'])
  public onWheel(event: Event): void {
    this.genericService.wheel$.next(event);
  }

  @HostListener('window:touchstart', ['$event'])
  public onTouchstart(event: Event): void {
    this.genericService.touchmove$.next(event);
  }

  @HostListener('window:touchend', ['$event'])
  public onTouchend(event: Event): void {
    this.genericService.touchmove$.next(event);
  }

  @HostListener('window:touchmove', ['$event'])
  public onTouchmove(event: Event): void {
    this.genericService.touchmove$.next(event);
  }

  @HostListener('window:beforeprint', ['$event'])
  public onBeforePrint() {
    this.genericService.isPrinting$.next(true);
  }

  @HostListener('window:afterprint', ['$event'])
  public onAfterPrint() {
    this.genericService.isPrinting$.next(false);
  }

  @HostListener('window:beforeunload', ['$event'])
  public unloadNotification($event: any) {
    if (this.apiService.isAnyPendingQueries) {
      $event.returnValue = true;
    }
  }

  public get innerHeight(): number {
    return this.genericService.innerHeight;
  }

  public ngOnInit(): void {
    this.gtmService.initGtm();

    LocatorService.injector = this.injector;

    this.setParamsToGenericService(null);
    this.langService.initLanguage();

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: RouterEvent) => {
      if (event instanceof NavigationStart) {
        this.currentPath = location.pathname;

        if (this.isSidebarOpen) {
          this.closeSidebar();
        }

        if (this.isSidebarAsWindowOpen) {
          this.closeSidebarAsWindow();
        }
      }
      if (event instanceof NavigationEnd) {
        // see can-activate-pending-req.guard.ts
        this.document.body.classList.remove('with-confirm');

        const dateStarted = Number(localStorage.getItem(LOADING_STARTED_KEY));
        const dateFinished = new Date().getTime();

        const diffTime = Math.abs(dateFinished - dateStarted);

        if (!!localStorage.getItem(LOADING_STARTED_KEY)) {
          // track only first load
          this.loggerService.logTrace(`Page loaded in: ${diffTime}`, {
            loadTimeAPP: this.diffTimeAfterViewInit,
            loadTimeUI: diffTime,
            route: this.currentPath,
            isShouldSend: true,
          });
          this.loggerService.logMetric(`Page loaded`, diffTime, {
            loadTimeAPP: this.diffTimeAfterViewInit,
            loadTimeUI: diffTime,
            route: this.currentPath,
            isShouldSend: true,
          });

          localStorage.removeItem(LOADING_STARTED_KEY);
        }

        if (
          !!this.legalTermsDate &&
          !!this.privacyPolicyDate &&
          !event?.url.includes('legal-terms') &&
          !event?.url.includes('privacy-policy')
        ) {
          this.checkAcceptedTerms(this.legalTermsDate, this.privacyPolicyDate, false);
        }

        if (location.pathname.includes('lcl-get-quote')) {
          this.widgetsService.destroyWidgets();
        }

        this.appOfflineService.loadOfflineData();
      }

      if (event instanceof NavigationEnd) {
        this.ga.setPageView(event.urlAfterRedirects);
      }
    });

    const awaitTime = 1000;
    this.uiService.isLoadingAPIVal$
      .pipe(
        filter((valueAPI: boolean) => !!valueAPI),
        delay(awaitTime),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        // prevent any possible dangling queries & spinner
        if (!this.apiService.isAnyPendingQueriesWithFetch) {
          this.uiService.hideAPILoader();
        }
      });

    this.checkVersion();

    this.loadAppBetaFlags();

    this.appService.isLg$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => (this.isLg = val));
    this.appService.isXlg$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => (this.isXlg = val));
    this.appService.isPurple$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => (this.isPurple = val));
    this.appService.isWindowMd$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => (this.isWindowMd = val));
    this.appService.isOpen$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => (this.isSidebarOpen = val));
    this.appService.options$.pipe(takeUntil(this.destroy$)).subscribe((options: ISidebarOptions) => {
      this.sidebarHeaderText = options?.sidebarHeaderText;
      this.isSidebarHeaderTitleCase = options?.isSidebarHeaderTitleCase;
      this.tooltipText = options.tooltipText;
      this.customZIndex = options?.customZIndex;
      this.hideBackDrop = options?.hideBackDrop;
    });

    this.appService.isOpenSidebarAsWindow$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => (this.isSidebarAsWindowOpen = val));
    this.appService.optionsSidebarAsWindow$.pipe(takeUntil(this.destroy$)).subscribe((options: ISidebarOptions) => {
      this.sidebarAsWindowHeaderText = options?.sidebarHeaderText;
      this.isSidebarHeaderTitleCase = options?.isSidebarHeaderTitleCase;
      this.sidebarAsWindowCustomZIndex = options?.customZIndex;
      this.isWindowFillMobile = !!options?.isFillMobile;
      this.hideBackDrop = options?.hideBackDrop;
    });

    this.actions$
      .pipe(ofActionSuccessful(FetchLegalTermsAndPrivacyPolicyUpdatedDateSuccess), takeUntil(this.destroy$))
      .subscribe(
        ({
          payload: { legalTermsDate, privacyPolicyDate, redirect },
        }: {
          payload: { legalTermsDate: string; privacyPolicyDate: string; redirect: boolean };
        }) => {
          this.legalTermsDate = legalTermsDate;
          this.privacyPolicyDate = privacyPolicyDate;

          this.checkAcceptedTerms(legalTermsDate, privacyPolicyDate, redirect);
        },
      );

    this.store.dispatch(new FetchLegalTermsAndPrivacyPolicyUpdatedDate());

    this.loadImports();
    this.companiesService.companyChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.batchImportService.setBatchImports([]);
      this.batchImportService.setCheckBatchImports();
    });

    this.lhcbMigrationPopupService.init();

    this.appOfflineService.loadOfflineData();
  }

  public ngAfterViewInit(): void {
    this.document.body.classList.remove(this.isLoadingBodyClass);

    const dateStarted = Number(localStorage.getItem(LOADING_STARTED_KEY));
    const dateFinished = new Date().getTime();

    this.diffTimeAfterViewInit = Math.abs(dateFinished - dateStarted);

    this.appService.setSidebarForm(this.sidebarForm);
    this.appService.setSidebarAsWindowForm(this.sidebarAsWindowForm);

    if (!location.pathname.includes('lcl-get-quote')) {
      this.loadCookieBot().pipe(takeUntil(this.destroy$)).subscribe();
    }

    this.appService.wchatUnderSidebar.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => {
      if (!!val) {
        this.widgetsService.setWidgetUnder();
      } else {
        this.widgetsService.setWidgetBelow();
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public closeSidebar(isClickOnClose?: boolean): void {
    this.appService.closeSidebar(isClickOnClose);
  }

  public closeSidebarAsWindow(): void {
    this.appService.closeSidebarAsWindow();
  }

  private checkVersion(): void {
    this.checkSwUpdate.checkVersion$
      .pipe(
        switchMap(() => this.apiService.get('version.json', null, { isShowLoader: false })),
        takeUntil(this.destroy$),
      )
      .subscribe((data: any) => {
        const lastVersion: string = localStorage.getItem('version-raw');
        if ([enumEnvironmentName.LOCAL_DEV, enumEnvironmentName.DEV, enumEnvironmentName.TEST].includes(environment.name)) {
          console.log('⚠ Check Version. Old: ', lastVersion, ', ', data.version, ', should reload: ', data && data.version !== lastVersion);
        }
        this.genericService.appVersion$.next(data);
        if (lastVersion && data && data.version !== lastVersion) {
          this.loggerService.logEvent(`NEW_VERSION_FOUND`, {
            route: location.pathname,
            lastVersion: lastVersion,
            version: data.version,
            externalCompanyId: this.companiesService.companyExternalIds?.externalCompanyId,
            externalUserId: this.companiesService.userExternalId,
            acceptedTermsDate: this.companiesService.userLegalTermsAcceptedDate,
            acceptedPolicyDate: this.companiesService.userPrivacyPolicyAcceptedDate,
            timestamp: new Date(),
            isShouldSend: true,
          });
          this.checkSwUpdate.clearCache();
          localStorage.setItem('version-raw', data.version);
          location.reload();
        }
      });
  }

  private setParamsToGenericService(event: Event): void {
    this.genericService.innerWidth = window.innerWidth;
    this.genericService.innerHeight = window.innerHeight;
    if (event) {
      this.genericService.resize = event;
    }
  }

  private checkAcceptedTerms(legalTermsDate: string, privacyPolicyDate: string, redirect: boolean): void {
    const isUserAcceptLegalTerms: boolean = this.legalTermsService.isUserAcceptLegalTerms(legalTermsDate);
    const isUserAcceptPrivacyPolicy: boolean = this.legalTermsService.isUserAcceptPrivacyPolicy(privacyPolicyDate);

    if (!isUserAcceptLegalTerms || !isUserAcceptPrivacyPolicy) {
      this.checkSwUpdate.checkVersion$.next(true);

      this.uiService.hideAPILoader();
      this.uiService.hideUILoader();

      this.legalTermsService.redirectToLegalTermsAndPolicyConfirm(isUserAcceptLegalTerms, isUserAcceptPrivacyPolicy, redirect);
    }
  }

  private loadCookieBot(): Observable<any> {
    const isScriptExist: boolean = !![...(this.document.scripts as any)].find(
      (script: HTMLScriptElement) => script.id === this.consentScriptId,
    );
    if (isScriptExist) {
      return from(Promise.resolve());
    }
    const scriptPromise: Promise<any> = new Promise((resolve: any) => {
      const scriptSrc: HTMLScriptElement = this.renderer2.createElement('script');
      scriptSrc.id = this.consentScriptId;
      scriptSrc.src = 'https://consent.cookiebot.com/uc.js';
      scriptSrc.setAttribute('data-cbid', environment.cookieBotKey);
      scriptSrc.onload = resolve;
      this.renderer2.appendChild(this.document.head, scriptSrc);
    });

    return from(scriptPromise);
  }

  private loadImports(): void {
    this.batchImportService.getCheckBatchImports$
      .pipe(
        switchMap(() => {
          if (
            this.hasImportFeature &&
            this.applicationModeService.activeApplicationMode === enumApplicationMode.USER &&
            !!this.companiesService.getCompany()?.id
          ) {
            return this.batchImportRepository
              .getBatchImport(this.companiesService.getCompany()?.id)
              .pipe(map((res: PaginatedResultModel<BatchImportModel>) => res.items));
          } else {
            return of([]);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((data: BatchImportModel[]) => {
        this.batchImportService.setBatchImports(data);
      });

    timer(DEFAULT_STATUS_PING_TIME, DEFAULT_STATUS_PING_TIME)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.batchImportService.setCheckBatchImports();
      });
  }

  private get hasImportFeature(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.importCustomsFromExcelFeature);
  }

  private loadAppBetaFlags(): void {
    this.store.dispatch(new FetchAppBetaFlags());
  }
}
